import { DataLayerService } from "../../../services/data-layer-service";
import { LocalizationService } from "../../../services/localization-service";
import { FieldController } from "./field-controller";
import { ValidationRule } from "./validation-rule.interface";

export class FormController {
    private readonly _localizationService: LocalizationService;
    private readonly _dataLayerService: DataLayerService;
    private fields: Map<string, FieldController> = new Map();

    constructor(localizationService: LocalizationService, dataLayerService: DataLayerService) {
        this._localizationService = localizationService;
        this._dataLayerService = dataLayerService;
    }

    public register(field: string, options: {
        inputId: string,
        errorId: string,
        rules: ValidationRule[],
        debounceMs?: number
    }): void {
        const input = document.getElementById(options.inputId) as HTMLInputElement;
        const error = document.getElementById(options.errorId) as HTMLElement;
        if (!input || !error) throw new Error(`Input or error element not found for '${field}'`);
        const controller = new FieldController(this._localizationService, this._dataLayerService, input, error, options.rules, options.debounceMs);
        this.fields.set(field, controller);
    }

    public getValue(field: string): string {
        return this.fields.get(field)?.getValue() ?? "";
    }

    public setValue(field: string, value: string): void {
        this.fields.get(field)?.setValue(value);
    }

    public getValues(): Record<string, string> {
        const values: Record<string, string> = {};
        this.fields.forEach((controller, name) => {
            values[name] = controller.getValue();
        });
        return values;
    }

    public watch(field: string, fn: (value: string) => void): void {
        this.fields.get(field)?.onChange(fn);
    }

    public async validate(): Promise<boolean> {
        let isValid = true;

        for (const [, field] of this.fields) {
            const valid = await field.validate(true, true);
            isValid = isValid && valid;
        }

        return isValid;
    }

    public destroy(): void {
        this.fields.forEach(f => f.destroy());
        this.fields.clear();
    }
}
